import { useState, useEffect, useCallback } from 'react';
import { fetchApi } from '../../lib/api';

export default function CompanyDetail({ companyId, onUpdate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [roleValue, setRoleValue] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchApi(`/api/admin/companies/${companyId}/detail`);
      setData(result);
      setForm(result.profile || {});
      setRoleValue(result.user?.role || '');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await fetchApi(`/api/admin/companies/${companyId}/profile`, {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      setEditing(false);
      await load();
      onUpdate && onUpdate();
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (newRole) => {
    try {
      await fetchApi(`/api/admin/users/${companyId}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole }),
      });
      setRoleValue(newRole);
      await load();
      onUpdate && onUpdate();
    } catch (err) {
      alert('Role change failed: ' + err.message);
    }
  };

  if (loading) return <div className="text-sm text-gray-400">Loading...</div>;
  if (!data) return <div className="text-sm text-gray-400">Company not found</div>;

  const { user, profile, connections, invites, nda_records } = data;
  const inputCls = "w-full px-3 py-2 rounded-input border-2 border-vault-border text-sm text-vault-navy bg-white focus:border-vault-gold focus:outline-none";
  const labelCls = "block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1";

  return (
    <div className="space-y-8">
      {/* Profile Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-vault-navy">Company Profile</h3>
          {!editing ? (
            <button onClick={() => setEditing(true)} className="text-vault-gold text-sm font-semibold">Edit</button>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => setEditing(false)} className="text-gray-400 text-sm">Cancel</button>
              <button onClick={saveProfile} disabled={saving} className="text-vault-gold text-sm font-semibold">{saving ? 'Saving...' : 'Save'}</button>
            </div>
          )}
        </div>

        {editing ? (
          <div className="space-y-3">
            <div><label className={labelCls}>Company Name</label><input className={inputCls} value={form.company_name || ''} onChange={e => setForm({...form, company_name: e.target.value})} /></div>
            <div><label className={labelCls}>Type</label>
              <select className={inputCls} value={form.company_type || ''} onChange={e => setForm({...form, company_type: e.target.value})}>
                <option value="">—</option>
                <option value="manufacturer">Manufacturer</option>
                <option value="retailer">Retailer</option>
                <option value="agent">Agent</option>
                <option value="distributor">Distributor</option>
              </select>
            </div>
            <div><label className={labelCls}>Website</label><input className={inputCls} value={form.website || ''} onChange={e => setForm({...form, website: e.target.value})} /></div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            {profile?.logo_url && <img src={profile.logo_url} alt="" className="h-12 rounded mb-2" />}
            <div><span className="text-gray-400 w-24 inline-block">Name:</span> <span className="text-vault-navy font-medium">{profile?.company_name}</span></div>
            <div><span className="text-gray-400 w-24 inline-block">Email:</span> <span className="text-vault-navy">{user?.email}</span></div>
            <div><span className="text-gray-400 w-24 inline-block">Type:</span> <span className="text-vault-navy capitalize">{profile?.company_type || '—'}</span></div>
            <div><span className="text-gray-400 w-24 inline-block">Website:</span> {profile?.website ? <a href={profile.website} target="_blank" rel="noreferrer" className="text-vault-gold hover:underline">{profile.website}</a> : '—'}</div>
            <div><span className="text-gray-400 w-24 inline-block">NDA:</span> {profile?.nda_document_url ? <a href={profile.nda_document_url} target="_blank" rel="noreferrer" className="text-vault-gold hover:underline">View NDA</a> : profile?.nda_link ? <a href={profile.nda_link} target="_blank" rel="noreferrer" className="text-vault-gold hover:underline">External NDA</a> : '—'}</div>
            <div><span className="text-gray-400 w-24 inline-block">Countersign:</span> {profile?.countersign_required ? 'Required' : 'Acknowledge only'}</div>
            <div><span className="text-gray-400 w-24 inline-block">NDA Version:</span> v{profile?.nda_version || 1}</div>
            <div><span className="text-gray-400 w-24 inline-block">Approved:</span> {profile?.approved_at ? new Date(profile.approved_at).toLocaleDateString() : 'Not yet'}</div>
            {profile?.categories?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {profile.categories.map((cat, i) => <span key={i} className="px-2 py-0.5 bg-gray-100 rounded-full text-[10px] text-gray-500">{cat}</span>)}
              </div>
            )}
          </div>
        )}

        {/* Role changer */}
        <div className="mt-4 pt-4 border-t border-vault-border">
          <label className={labelCls}>Role</label>
          <select className={inputCls + " max-w-xs"} value={roleValue} onChange={e => changeRole(e.target.value)}>
            <option value="pending_company">Pending Company</option>
            <option value="company">Company (Approved)</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>

      {/* Connections */}
      <div>
        <h3 className="font-semibold text-vault-navy mb-3">Inventor Connections ({connections.length})</h3>
        {connections.length === 0 ? <p className="text-sm text-gray-400">None</p> : (
          <div className="space-y-2">
            {connections.map(c => (
              <div key={c.id} className="border border-vault-border rounded-btn p-3 flex items-center justify-between text-sm">
                <div>
                  <span className="font-medium text-vault-navy">{c.inventor?.display_name}</span>
                  <span className="text-gray-400 ml-2">{c.inventor?.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${c.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : c.status === 'pending' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600'}`}>{c.status}</span>
                  {c.nda_agreed && <span className="text-[10px] text-emerald-600">NDA v{c.nda_version_agreed}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invites */}
      <div>
        <h3 className="font-semibold text-vault-navy mb-3">Invites ({invites.length})</h3>
        {invites.length === 0 ? <p className="text-sm text-gray-400">None</p> : (
          <div className="space-y-2">
            {invites.map(inv => (
              <div key={inv.id} className="border border-vault-border rounded-btn p-3 flex items-center justify-between text-sm">
                <span className="text-gray-500">{inv.invited_email || 'Generic link'}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${inv.used_at ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                  {inv.used_at ? `Claimed by ${inv.claimed_inventor?.display_name}` : 'Active'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* NDA Records */}
      <div>
        <h3 className="font-semibold text-vault-navy mb-3">NDA Records ({nda_records.length})</h3>
        {nda_records.length === 0 ? <p className="text-sm text-gray-400">None</p> : (
          <div className="space-y-2">
            {nda_records.map(nda => (
              <div key={nda.id} className="border border-vault-border rounded-btn p-3 text-sm space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-vault-navy">NDA v{nda.nda_version}</span>
                  {nda.document_url && <a href={nda.document_url} target="_blank" rel="noreferrer" className="text-vault-gold text-xs font-semibold hover:underline">View Document</a>}
                </div>
                {nda.inventor_signed_at && (
                  <div className="text-xs text-gray-500">
                    Inventor signed: <span className="text-vault-navy">{nda.inventor_signature_text}</span> — {new Date(nda.inventor_signed_at).toLocaleString()} — IP: {nda.inventor_ip_address}
                  </div>
                )}
                {nda.company_action_at && (
                  <div className="text-xs text-gray-500">
                    Company {nda.company_action_type}: <span className="text-vault-navy">{nda.company_signature_text}</span> — {new Date(nda.company_action_at).toLocaleString()} — IP: {nda.company_ip_address}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
